import {Body2} from '@ui/atoms/typography';
import {MultiField} from '@ui/molecules/field/multifield/multifield';
import {CancelIcon, MinusIcon} from '@ui/atoms/icons';
import {TextField} from '@ui/molecules/field/textfield';
import {
  BlockContent,
  BlockDragRail,
  QuestionCard,
  DragIcon,
  QuestionBlockHeader,
  QuestionTypePill,
  RemoveRow,
  RemoveIcon,
  AdditionalHint,
  CompatibilityError,
  RemoveButton,
} from './content-step.styles';
import {ChecklistData, ContentBlock} from './types';

interface ChecklistBlockProps {
  compact?: boolean;
  block: Extract<ContentBlock, {type: 'checklist'}>;
  blocks: ContentBlock[];
  showValidation?: boolean;
  dragHandleProps: any;
  isDragging: boolean;
  onBlocksChange: (blocks: ContentBlock[]) => void;
  onRemove: () => void;
  createChecklistData: () => ChecklistData;
  incompatible?: boolean;
}

export const ChecklistBlock = ({
  compact = false,
  block,
  blocks,
  showValidation,
  dragHandleProps,
  isDragging,
  onBlocksChange,
  onRemove,
  createChecklistData,
  incompatible = false,
}: ChecklistBlockProps) => {
  return (
    <QuestionCard isDragging={isDragging} data-block-type={block.type}>
      <BlockDragRail {...dragHandleProps} aria-label="Reorder checklist">
        <DragIcon aria-hidden="true">⠿</DragIcon>
      </BlockDragRail>
      <BlockContent>
        {incompatible && (
          <CompatibilityError role="alert">
            Not compatible with Anonymous visibility. Remove this block or
            change visibility.
          </CompatibilityError>
        )}
        <QuestionBlockHeader>
          <QuestionTypePill variant="checklist">Checklist</QuestionTypePill>
          <RemoveRow onClick={onRemove} aria-label="Remove checklist">
            <RemoveIcon>
              <MinusIcon />
            </RemoveIcon>{' '}
            Remove
          </RemoveRow>
        </QuestionBlockHeader>
        {(() => {
          const checklistTitleError =
            showValidation && !block.checklistData?.title?.trim();
          return (
            <TextField
              label="Title"
              placeholder="e.g Pre-release tasks"
              value={block.checklistData?.title || ''}
              onChange={(event) =>
                onBlocksChange(
                  blocks.map((b) => {
                    if (b.id !== block.id || b.type !== 'checklist') {
                      return b;
                    }
                    return {
                      ...b,
                      checklistData: {
                        ...(b.checklistData || createChecklistData()),
                        title: event.target.value,
                      },
                    };
                  }),
                )
              }
              state={checklistTitleError ? 'error' : 'default'}
              helper={checklistTitleError ? 'Add a checklist title' : undefined}
              inputStyle={{
                borderRadius: '10px',
                ...(compact
                  ? {
                      height: '44px',
                      padding: '5px 12px',
                      fontSize: '14px',
                      lineHeight: '22px',
                    }
                  : {}),
              }}
              margin
            />
          );
        })()}
        <div className="gap-2 flex flex-col">
          <Body2 weight="semibold">
            Add list <span style={{fontWeight: 400}}>(optional)</span>
          </Body2>
          <MultiField
            label=""
            id={`checklist-${block.id}`}
            value={(block.checklistData?.checklist || []).map((item, idx) => ({
              text: item.text,
              key: String(idx),
            }))}
            updateValue={(value?: Array<any>) => {
              const updated =
                value?.map((item) => ({text: item?.text || ''})) || [];
              onBlocksChange(
                blocks.map((b) => {
                  if (b.id !== block.id || b.type !== 'checklist') {
                    return b;
                  }
                  return {
                    ...b,
                    checklistData: {
                      ...(b.checklistData || createChecklistData()),
                      checklist: updated,
                    },
                  };
                }),
              );
            }}
            fontWeight={400}
            colorString="textMedium"
            checkedIndexes={[]}
            showCheckbox={true}
            userCanReorder={false}
            size="compact"
            placeholder="Add a task..."
            addFieldPlaceholder="Add item"
            compactSpacing
            dropdownComponent={(onHandleRemove: () => void) => (
              <RemoveButton type="button" onClick={onHandleRemove}>
                <CancelIcon style={{width: 24, height: 24}} color="#5F5F8C" />
              </RemoveButton>
            )}
            margin
          />
        </div>

        <AdditionalHint>
          Participants can also add their own items.
        </AdditionalHint>
      </BlockContent>
    </QuestionCard>
  );
};
